import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { t } from "../../translations";
import "./select_academy.css";

const SelectAcademy = ({ lang }) => {
  const [isExpanded, setIsExpanded] = useState({});

  const toggleExpanded = (index) => {
    setIsExpanded((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  useEffect(() => {
    // Check if Stripe script is already loaded
    if (document.querySelector('script[src*="buy-button.js"]')) {
      return;
    }

    // Load Stripe buy button script
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/buy-button.js";
    script.async = true;

    // Add error handling
    script.onerror = () => {
      // Failed to load Stripe buy button script
    };

    script.onload = () => {
      // Stripe buy button script loaded successfully
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup script on component unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const faqData = [
    {
      question: t("academy.faq_1_question", lang),
      answer: t("academy.faq_1_answer", lang)
    },
    {
      question: t("academy.faq_2_question", lang),
      answer: t("academy.faq_2_answer", lang)
    },
    {
      question: t("academy.faq_3_question", lang),
      answer: t("academy.faq_3_answer", lang)
    },
    {
      question: t("academy.faq_4_question", lang),
      answer: t("academy.faq_4_answer", lang)
    },
    {
      question: t("academy.faq_5_question", lang),
      answer: t("academy.faq_5_answer", lang)
    },
    {
      question: t("academy.faq_6_question", lang),
      answer: t("academy.faq_6_answer", lang)
    },
    {
      question: t("academy.faq_7_question", lang),
      answer: t("academy.faq_7_answer", lang)
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t("academy.seo_title", lang)}</title>
        <meta name="description" content={t("academy.seo_description", lang)} />
        <meta name="keywords" content={t("academy.seo_keywords", lang)} />
        <link rel="canonical" href="https://panichefs.com/panichefs-academy" />
        <meta property="og:title" content={t("academy.og_title", lang)} />
        <meta
          property="og:description"
          content={t("academy.og_description", lang)}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://panichefs.com/panichefs-academy"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("academy.twitter_title", lang)} />
        <meta
          name="twitter:description"
          content={t("academy.twitter_description", lang)}
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            name: t("academy.structured_data_name", lang),
            description: t("academy.structured_data_description", lang),
            url: "https://panichefs.com/panichefs-academy",
            address: {
              "@type": "PostalAddress",
              streetAddress: t("academy.contact_address", lang).split("\n")[0],
              addressLocality: t("academy.contact_address", lang).split(
                "\n"
              )[1],
              addressCountry: "Canada"
            },
            telephone: t("academy.contact_phone", lang),
            email: t("academy.contact_email", lang),
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: t("academy.structured_data_catalog_name", lang),
              itemListElement: [
                {
                  "@type": "Course",
                  name: t("academy.structured_data_course_name", lang),
                  description: t(
                    "academy.structured_data_course_description",
                    lang
                  ),
                  provider: {
                    "@type": "Organization",
                    name: t("academy.structured_data_name", lang)
                  },
                  offers: {
                    "@type": "Offer",
                    price: "375",
                    priceCurrency: "CAD"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <div className="academy-container">
        <div className="academy-content">
          {/* Header Section */}
          <div className="academy-header">
            <h1 className="academy-title">{t("academy.title", lang)}</h1>
            <h2 className="academy-subtitle">{t("academy.subtitle", lang)}</h2>
            <div className="info-paragraph">
              <p>{t("academy.intro_question", lang)}</p>
            </div>
            <div className="info-paragraph">
              <p>{t("academy.platform_description", lang)}</p>
            </div>
            <div className="info-paragraph">
              <p>{t("academy.certificate_requirement", lang)}</p>
            </div>
          </div>

          {/* What is Food Handler Certificate Section */}
          <div className="academy-section">
            <h3 className="academy-section-title">
              {t("academy.what_is_certificate_title", lang)}
            </h3>
            <div className="content-box content-box-wide">
              <p>{t("academy.certificate_definition", lang)}</p>
              <p>{t("academy.certificate_purpose", lang)}</p>
            </div>
          </div>

          {/* Certificate Objectives Section */}
          <div className="academy-section">
            <h4 className="academy-section-subtitle">
              {t("academy.certificate_objectives_title", lang)}
            </h4>
            <div className="content-box content-box-wide">
              <ul className="content-list">
                <li>{t("academy.objective_1", lang)}</li>
                <li>{t("academy.objective_2", lang)}</li>
                <li>{t("academy.objective_3", lang)}</li>
                <li>{t("academy.objective_4", lang)}</li>
                <li>{t("academy.objective_5", lang)}</li>
              </ul>
            </div>
          </div>

          {/* Who is this for Section */}
          <div className="academy-section">
            <h4 className="academy-section-subtitle">
              {t("academy.who_is_this_for_title", lang)}
            </h4>
            <div className="content-box content-box-wide">
              <ul className="content-list">
                <li>{t("academy.target_1", lang)}</li>
                <li>{t("academy.target_2", lang)}</li>
                <li>{t("academy.target_3", lang)}</li>
                <li>{t("academy.target_4", lang)}</li>
                <li>{t("academy.target_5", lang)}</li>
                <li>{t("academy.target_6", lang)}</li>
                <li>{t("academy.target_7", lang)}</li>
                <li>{t("academy.target_8", lang)}</li>
                <li>{t("academy.target_9", lang)}</li>
                <li>{t("academy.target_10", lang)}</li>
              </ul>
            </div>
          </div>

          {/* Registration Section */}
          <div className="registration-section">
            <div style={{ textAlign: "center", marginBottom: "50px" }}>
              <h3 className="registration-title">
                {t("academy.registration_section_title", lang)}
              </h3>
            </div>

            {/* Registration Fee Section */}
            <div className="registration-box">
              <h4 className="registration-fee-title">
                {t("academy.registration_fee_title", lang)}
              </h4>
              <div className="registration-fee-amount">
                {t("academy.registration_fee_amount", lang)}
              </div>
              <h5 className="fee-covers-title">
                {t("academy.fee_covers_title", lang)}
              </h5>
              <ul className="fee-covers-list">
                <li>{t("academy.fee_covers_1", lang)}</li>
                <li>{t("academy.fee_covers_2", lang)}</li>
                <li>{t("academy.fee_covers_3", lang)}</li>
                <li>{t("academy.fee_covers_4", lang)}</li>
                <li>{t("academy.fee_covers_5", lang)}</li>
              </ul>
              {lang === "fr" ? (
                <button
                  onClick={() =>
                    window.open(
                      "https://buy.stripe.com/3cI7sK6lp2QZ2g48qo3wQ02",
                      "_blank"
                    )
                  }
                  className="register-button"
                >
                  {t("academy.register_now_button", lang)}
                </button>
              ) : (
                <button
                  onClick={() =>
                    window.open(
                      "https://buy.stripe.com/dRmeVc8txgHPaMA8qo3wQ03",
                      "_blank"
                    )
                  }
                  className="register-button"
                >
                  {t("academy.register_now_button", lang)}
                </button>
              )}
              <p className="contact-instructions">
                {lang === "fr"
                  ? "Paiement sécurisé et instantané avec Stripe"
                  : "Secure and instant payment with Stripe"}
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3 className="faq-title">{t("academy.faq_title", lang)}</h3>

            {faqData.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  onClick={() => toggleExpanded(index)}
                  className="faq-button"
                >
                  <span>{faq.question}</span>
                  <span
                    className={`faq-icon ${isExpanded[index] ? "expanded" : ""}`}
                  >
                    +
                  </span>
                </button>
                {isExpanded[index] && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectAcademy;
